import { t } from "@lingui/macro";
import { defaultResumeData } from "@stylingcv/schema";
import { Notepad, Question, Info } from "@phosphor-icons/react";
import { RichInput, Button, Tooltip } from "@stylingcv/ui";
import { cn } from "@stylingcv/utils";

import { useResumeStore } from "@/client/stores/resume";

export const NotesSection = () => {
  const setValue = useResumeStore((state) => state.setValue);
  const notes = useResumeStore((state) => {
    try {
      return state.resume.data.metadata.notes || defaultResumeData.metadata.notes;
    } catch {
      return defaultResumeData.metadata.notes;
    }
  });

  const updateNotes = (content: string) => {
    try {
      setValue("metadata.notes", content);
    } catch {
      // Silent fail
    }
  };

  return (
    <div className="space-y-4">

      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200 pb-5">
        <div className="flex items-center gap-3">
          <div className="flex size-10 items-center justify-center rounded-xl bg-gray-100">
            <Notepad size={20} className="text-gray-600" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-[#16344c]">{t`Notes`}</h3>
            <p className="text-[11px] text-gray-400">{t`Private notes`}</p>
          </div>
        </div>
        <Tooltip content={t`Learn more about notes`}>
          <Button size="icon" variant="ghost" className="h-8 w-8 text-gray-400 hover:bg-gray-100 hover:text-[#16344c] transition-colors">
            <Question size={18} />
          </Button>
        </Tooltip>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-6">

        <div className="p-4 rounded-xl border border-gray-200 bg-gray-50 flex items-start gap-3">
          <div className="mt-0.5">
            <Info size={18} weight="fill" className="text-gray-400" />
          </div>
          <div className="space-y-1">
            <h4 className="text-sm font-semibold text-[#16344c]">{t`Private Notes`}</h4>
            <p className="text-[10px] text-gray-500 leading-relaxed">
              {t`This section is reserved for your personal notes specific to this resume. The content here remains private and is not shared with anyone else. For example, information regarding which companies you sent this resume to or links to job descriptions.`}
            </p>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm">
          <div className="p-2 border-b border-gray-200 bg-gray-50 flex items-center gap-2">
            <div className="flex gap-1.5 ml-2">
              <div className="w-2 h-2 rounded-full bg-red-500/50"></div>
              <div className="w-2 h-2 rounded-full bg-yellow-500/50"></div>
              <div className="w-2 h-2 rounded-full bg-green-500/50"></div>
            </div>
            <span className="text-[10px] text-gray-400 uppercase tracking-widest ml-2 font-bold font-mono">My Notes</span>
          </div>
          <div className="p-4 relative">
            <div className="relative z-10 [&_.ProseMirror]:min-h-[200px] [&_.ProseMirror]:text-sm [&_.ProseMirror]:text-[#16344c] [&_.ProseMirror]:leading-relaxed focus-within:[&_.ProseMirror]:outline-none">
              <RichInput
                content={notes || ""}
                onChange={updateNotes}
              />
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};
